/* eslint no-unused-vars: ["warn"] */

import * as Sentry from '@sentry/browser';
import * as CurrentPatientActions from '../actions/current-patient-actions.ts';
import * as GroupActions from '../actions/group-actions.ts';
import * as hospitalBookingActions from '../../ng2/hospital-booking/store/hospital-booking.actions';
import * as NotificationsActions from '../actions/notifications-actions';
import * as ReasonsActions from '../../ng2/main/reasons/reasons.actions.ts';
import * as appointmentRequestsActions from '../../ng2/home/appointment-requests/appointment-requests.actions';
import { authenticatedPatientSelector, currentPatientSelector } from '../selectors/patients-selectors.ts';
import { resetOffering, setOffering } from '../actions/offering-actions';
import { SetLocale } from '../../ng2/main/locale/locale.actions';


module.exports = ['$urlRouterProvider', '$stateProvider', ($urlRouterProvider, $stateProvider) => {
  // UI Router navigation
  $urlRouterProvider.otherwise('not-found');

  $stateProvider
    .state('root', {
      abstract: true,
      url: '?groupId&locale&token',
      resolve: {
        statePublicGroup: [
          '$q',
          '$ngRedux',
          '$stateParams',
          'Groups',
          'ProductService',
          (
            $q,
            $ngRedux,
            $stateParams,
            Groups,
            ProductService,
          ) => $q((resolve, reject) => {
            const groupId = parseInt($stateParams.groupId);
            const locale = $stateParams.locale ? $stateParams.locale : 'en';

            if (groupId) {
              Groups.get({ id: groupId, locale }).$promise.then((group) => {
                $ngRedux.dispatch(GroupActions.setPublicGroup(group));
                $ngRedux.dispatch(new hospitalBookingActions.SetIsHospitalBooking(ProductService.isAvailable('HOSPITAL_BOOKING')));
                resolve(group);
              }, () => reject());
            } else {
              resolve();
            }
          }),
        ],
        stateAuthToken: [
          '$q',
          '$rootScope',
          '$state',
          '$stateParams',
          'SecurityService',
          'SessionService',
          (
            $q,
            $rootScope,
            $state,
            $stateParams,
            SecurityService,
            SessionService,
          ) => $q((resolve) => {
            if (!$stateParams.token) {
              resolve();
              return;
            }

              // If URL contains an auth token, authenticate user using token
            SecurityService.authenticateWithToken($stateParams.token).then((response) => {
              SessionService.store(response.data.session);

              resolve();
            }, () => $rootScope.$emit('application:out'));
          }),
        ],
        stateSession: [
          '$ngRedux',
          '$q',
          '$rootScope',
          '$stateParams',
          'Groups',
          'PatientAppContextService',
          'ProductService',
          'SecurityService',
          'SessionService',
          // Root state resolve dependencies
          'stateAuthToken',
          'statePublicGroup',
          (
            $ngRedux,
            $q,
            $rootScope,
            $stateParams,
            Groups,
            PatientAppContextService,
            ProductService,
            SecurityService,
            SessionService,
            stateAuthToken,
            statePublicGroup,
          ) => $q((resolve) => {
            // Skip if session is already initialized or user is not authenticated
            if (SessionService.isInitialized()) {
              resolve();
              return;
            }

            SessionService.load().then(() => {
              resolve();
            });
          }).then(() => {
            // Replace public group in Redux with group in session
            if (SessionService.isInitialized()) {
              const group = _.find(SessionService.fetch().groups, { id: +$stateParams.groupId });
              if (group) {
                $ngRedux.dispatch(GroupActions.setCurrentGroup(new Groups(group)));
                $ngRedux.dispatch(new hospitalBookingActions.SetIsHospitalBooking(ProductService.isAvailable('HOSPITAL_BOOKING')));
              }
            }
          }),
        ],
        stateAuthenticatedPatient: [
          '$q',
          '$stateParams',
          'PatientsService',
          'SessionService',
          // Root state resolve dependency
          'stateSession',
          (
            $q,
            $stateParams,
            PatientsService,
            SessionService,
            stateSession,
          ) => $q((resolve) => {
            // Do not attempt to load subscription if user is not authenticated OR logged user is not registered in the current group
            if (!SessionService.isPatientLoggedIn() || !SessionService.isInitialized() || !_.some(SessionService.fetch().groups, { id: parseInt($stateParams.groupId) })) {
              Sentry.configureScope(scope => scope.setUser(null));

              resolve();
              return;
            }

            PatientsService.loadPatient(SessionService.fetch().authenticatedPatientId).then((authenticatedPatient) => {
              Sentry.configureScope(scope => scope.setUser(_.pick(authenticatedPatient, ['id', 'firstName', 'middleName', 'lastName', 'state'])));
              resolve(authenticatedPatient);
            });
          }),
        ],
        stateLocale: [
          '$ngRedux',
          '$state',
          '$rootScope',
          '$stateParams',
          '$window',
          'LanguageService',
          // Root state resolve dependency
          'stateAuthenticatedPatient',
          (
            $ngRedux,
            $state,
            $rootScope,
            $stateParams,
            $window,
            LanguageService,
            stateAuthenticatedPatient,
          ) => {
            let { locale } = $stateParams;
            const authenticatedPatient = authenticatedPatientSelector($ngRedux.getState());

            if (authenticatedPatient) {
              locale = authenticatedPatient.locale;
              if ($stateParams.locale) {
                $rootScope.$on('$stateChangeSuccess', () => {
                  if (authenticatedPatient.state === 'confirmed') {
                    $state.go('.', { locale: null }, { notify: false });
                  }
                });
              }
            }
            if (!locale) {
              locale = $ngRedux.getState().group.locale;
            }
            if (!locale) {
              locale = /^fr/.test($window.navigator.language) && application.env !== 'test' ? 'fr_CA' : 'en';
            }

            $ngRedux.dispatch(new SetLocale(locale));
            LanguageService.useLanguage(locale, false);
            LanguageService.isWaitingForSwitchCompletion = false;
          },
        ],
        stateProfile: [
          '$ngRedux',
          '$q',
          '$stateParams',
          'ProfileService',
          // Root state resolve dependency
          'stateAuthenticatedPatient',
          (
            $ngRedux,
            $q,
            $stateParams,
            ProfileService,
            stateAuthenticatedPatient,
          ) => $q((resolve) => {
            const authenticatedPatient = authenticatedPatientSelector($ngRedux.getState());

            if (!authenticatedPatient || authenticatedPatient.state !== 'confirmed') {
              resolve();
              return;
            }

            ProfileService.getNotification(authenticatedPatient, +$stateParams.groupId, (response) => {
              $ngRedux.dispatch(NotificationsActions.setNotifications(response.data));
              resolve();
            });
          }),
        ],
      },
      views: {
        '@': {
          template: '<main class="layout"></main>',
        },
      },
    })
    .state('not_found', {
      insecure: true,
      url: '/not-found',
      views: {
        '@': {
          templateUrl: 'main/templates/common/not-found.html',
        },
      },
    })
    .state('patient_agreement', {
      allowNoGroupId: true,
      parent: 'root',
      activated: true,
      insecure: true,
      url: '/patient-agreement',
      views: {
        'content@root': {
          template: '<patient-legal document="agreement-text"></patient-legal>',
        },
      },
    })
    .state('privacy_statement', {
      allowNoGroupId: true,
      parent: 'root',
      activated: true,
      insecure: true,
      url: '/privacy-statement',
      views: {
        'content@root': {
          template: '<patient-legal document="privacy-text"></patient-legal>',
        },
      },
    })
    .state('patients', {
      parent: 'root',
      url: '/patients',
      abstract: true,
    })
    .state('patients.id', {
      url: '/:patientId',
      abstract: true,
      resolve: {
        statePatient: [
          '$ngRedux',
          '$stateParams',
          '$rootScope',
          'SessionService',
          // Root state resolve dependency
          'stateAuthenticatedPatient',
          (
            $ngRedux,
            $stateParams,
            $rootScope,
            SessionService,
            stateAuthenticatedPatient,
          ) => {
            if (!SessionService.isInitialized() || !_.some(SessionService.fetch().groups, { id: parseInt($stateParams.groupId) })) {
              $rootScope.$emit('application:out', { error: 'patient_not_created' });
              return;
            }

            $ngRedux.dispatch(CurrentPatientActions.setPatient(+$stateParams.patientId));
          },
        ],
      },
    })
    .state('patients.id.reasons', {
      url: '/reasons?offeringId&reasonId',
      resolve: {
        stateReasons: [
          '$ngRedux',
          '$state',
          '$stateParams',
          '$timeout',
          'AppointmentReasons',
          'ProductService',
          'SessionService',
          // Root state resolve dependency
          'statePatient',
          (
            $ngRedux,
            $state,
            $stateParams,
            $timeout,
            AppointmentReasons,
            ProductService,
            SessionService,
            statePatient,
          ) => {
            const offeringId = parseInt($stateParams.offeringId) ? parseInt($stateParams.offeringId) : null;
            if (offeringId) {
              const groupId = $ngRedux.getState().group.id;
              const patientId = currentPatientSelector($ngRedux.getState()).id;

              const manageReasons = (reasons) => {
                $ngRedux.dispatch(new ReasonsActions.SetReasons(reasons));
                // When offering has only 1 reason, automatically select that reason
                if (!$stateParams.reasonId && reasons.length === 1) {
                  const reason = reasons[0];
                  $ngRedux.dispatch(new ReasonsActions.SetCurrentReason(reason));
                }
              };

              // Try to get reasons from the store and if not found, query them
              const reasons = $ngRedux.getState().reasons.list.filter(reason => reason.offering.id === offeringId);

              if (reasons.length) {
                manageReasons(reasons);
              } else {
                const deferred = AppointmentReasons.query({
                  group_id: groupId,
                  patient_id: patientId,
                  offering_id: offeringId,
                });
                deferred.$promise.then(manageReasons);
                return deferred.$promise;
              }
            }
            return true;
          },
        ],
      },
      views: {
        'content@root': {
          template: '<reasons></reasons>',
        },
      },
    })
    .state('patients.id.reasons.id', {
      url: '/:reasonId',
      abstract: true,
      resolve: {
        stateReason: [
          '$ngRedux',
          '$stateParams',
          'AppointmentReasons',
          // Root state resolve dependency
          'stateReasons',
          (
            $ngRedux,
            $stateParams,
            AppointmentReasons,
            stateReasons,
          ) => {
            const reasonId = parseInt($stateParams.reasonId);
            const currentReason = $ngRedux.getState().reasons.current;

            // Reload reason only if it doesn't exists in Redux or if the current reason is different
            if (!currentReason || currentReason.id !== reasonId) {
              return AppointmentReasons.get({ id: reasonId }).$promise
                .then(reason => $ngRedux.dispatch(new ReasonsActions.SetCurrentReason(reason)));
            }

            return true;
          },
        ],
      },
    })
    .state('patients.id.reasons.id.form', {
      url: '/form',
      views: {
        'content@root': {
          template: '<reason-form></reason-form>',
        },
      },
    })
    .state('patients.id.reasons.id.patients', {
      url: '/patients',
      views: {
        'content@root': {
          template: '<linked-patients></linked-patients>',
        },
      },
    })
    .state('patients.id.reasons.id.preferences', {
      url: '/preferences',
      views: {
        'content@root': {
          template: '<reason-preferences></reason-preferences>',
        },
      },
    })
    .state('patients.id.reasons.id.availabilities', {
      url: '/availabilities',
      views: {
        'content@root': {
          template: '<availabilities></availabilities>',
        },
      },
      resolve: {
        stateAvailabilities: [
          '$ngRedux',
          '$stateParams',
          'SessionService',
          ($ngRedux, $stateParams, SessionService) => {
            const offeringId = parseInt($stateParams.offeringId) ? parseInt($stateParams.offeringId) : null;

            // Generate new requestSessionUuid for availability query tracking
            if (offeringId) {
              $ngRedux.dispatch(resetOffering());
              $ngRedux.dispatch(setOffering({ id: offeringId }, SessionService.generateUuid()));
            }
          },
        ],
      },
    })
    .state('patients.id.appointments', {
      url: '/appointments',
      abstract: true,
    })
    .state('patients.id.appointments.confirmation', {
      url: '/confirmation',
      views: {
        'content@root': {
          template: '<appointments-confirmation></appointments-confirmation>',
        },
      },
    })
    .state('patients.id.appointments.id', {
      url: '/:appointmentId',
      views: {
        'content@root': {
          template: '<appointment-details></appointment-details>',
        },
      },
    })
    .state('patients.id.appointments.id.cancel', {
      url: '/cancel',
      views: {
        'content@root': {
          template: '<appointment-cancel></appointment-cancel>',
        },
      },
    })
    .state('patients.id.requests', {
      url: '/requests',
      abstract: true,
    })
    .state('patients.id.requests.id', {
      url: '/:requestId',
      abstract: true,
    })
    .state('patients.id.requests.id.cancel', {
      url: '/cancel',
      resolve: {
        stateRequest: [
          '$ngRedux',
          '$stateParams',
          (
            $ngRedux,
            $stateParams,
          ) => {
            const requestId = $stateParams.requestId;
            $ngRedux.dispatch(new appointmentRequestsActions.SelectAppointmentRequest(+requestId));
          },
        ],
      },
      views: {
        'content@root': {
          template: '<appointment-request-cancel></appointment-request-cancel>',
        },
      },
    });
}];



// WEBPACK FOOTER //
// ./main/config/routes.js